<template>
  <div class="volatility-container width-full overflow-auto">
    <PageSpinner v-if="loading" />
    <div v-else>
      <div class="header px-4 pt-4">
        <h1 :title="model?.name">{{ model?.name }}</h1>
        <div class="spacer" />
        <div id="kpiPanel" class="parameter-list">
          <div class="parameter">
            <div class="label text-gray-600">Financial Year-End</div>
            <div class="value">{{ filters.shorterdate(model?.financialYearEnd) }}</div>
          </div>
          <div class="parameter">
            <div class="label text-gray-600">Shares Outstanding</div>
            <div class="value">{{ filters.compactNumber(model?.sharesOutstanding) ?? '--' }}</div>
          </div>
          <div class="parameter">
            <div class="label text-gray-600">Annual Revenue</div>
            <div class="value">{{ filters.compactMoney(model?.annualRevenue) ?? '--' }}</div>
          </div>
          <div class="parameter">
            <div class="label text-gray-600">Maximum Tolerance</div>
            <div class="value">{{ filters.percentage(model?.maximumTolerance) }}</div>
          </div>
          <div class="parameter">
            <div class="label text-gray-600">Effective Tax Rate</div>
            <div class="value">{{ filters.percentage(model?.effectiveTaxRate) }}</div>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <div
          class="btn btn-sm flex items-center justify-items-start gap-1"
          @click="onBackButtonClick">
          <ChevronLeftIcon :size="15" />
          <span class="mr-1">Back</span>
        </div>

        <router-link v-if="canEditModel" class="btn btn-sm" :to="editRoute">
          <span class="">Edit Model</span>
        </router-link>
        <ExportMenu :items="exportMenuOptions" @export="handleExport" />
      </div>

      <!-- Overlay to prevent hover tooltips/interaction while exporting -->
      <div v-if="isExporting" class="absolute z-20 h-3/4 w-full opacity-0"></div>

      <div class="chart px-4 pt-4">
        <h2>Chart</h2>
        <fusioncharts
          id="volatilityChart"
          type="mscolumn2d"
          width="100%"
          height="400"
          dataFormat="json"
          :dataSource="dataSource"></fusioncharts>
      </div>
      <div class="details px-4 pt-8">
        <div class="content mb-8 mt-4">
          <div class="flex">
            <h2>Details</h2>
            <AppButton class="ml-2" @click="downloadTable">
              <Download :size="16" />
            </AppButton>
          </div>
          <div class="scenarios">
            <table id="vol-tbl">
              <thead>
                <tr>
                  <td colspan="2" class="pb-4">
                    <small class="block w-96 max-w-72">
                      Shown here are updated financial metrics should each retention scenario be fully realized and the
                      associated impact relative to the input/reported value.
                    </small>
                  </td>
                  <td colspan="2" v-for="(scenario, index) in scenarios" :key="scenario.title">
                    <div class="scenario-title">{{ scenario.title }}</div>
                    <div class="stats">
                      <div class="stat">
                        <span class="label text-gray-600">Tax Relief</span>
                        <span class="value" :data-test="`retention-${index}-tax-relief-value`">{{
                          filters.compactMoney(scenario.taxRelief)
                        }}</span>
                      </div>
                      <div class="stat">
                        <span class="label text-gray-600">After Tax</span>
                        <span class="value" :data-test="`retention-${index}-after-tax-value`">{{
                          filters.compactMoney(scenario.afterTax)
                        }}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="indicator-title-header py-2 text-gray-500">Performance Indicators</td>
                  <td class="indicator-value-header py-2 text-gray-500">
                    <div>Reported</div>
                  </td>
                  <template v-for="scenario in scenarios" :key="scenario.title">
                    <td class="impact-header color-gray-500 py-2">Impact</td>
                    <td
                      class="results-header color-gray-500 border-r border-solid border-gray-300 py-2 pr-4 last:border-r-0">
                      Results
                    </td>
                  </template>
                </tr>
                <tr class="values" v-for="indicator in indicators" :key="indicator.title">
                  <td class="indicator-title">{{ indicator.title }}</td>
                  <td class="indicator-value" v-if="indicator.percentage">
                    <div>{{ filters.percentage(indicator.value, 100, 1, 1) }}</div>
                  </td>
                  <td class="indicator-value" v-else>
                    <div>{{ filters.compactMoney(indicator.value) }}</div>
                  </td>
                  <template v-for="(scenario, index) in indicator.scenarios" :key="index">
                    <td class="impact" :class="{ 'font-bold text-primary': scenario.threshold }">
                      {{ filters.percentage(scenario.impact, 100, 1, 1) }}
                    </td>
                    <td
                      class="results border-r border-solid border-gray-300 pr-4 last:border-r-0"
                      v-if="indicator.warning">
                      <div class="flex justify-end items-center">
                        <div class="tooltip tooltip-left tooltip-warning" :data-tip="indicator.warning">
                          <CircleHelp class="text-warning" :size="16" />
                        </div>
                      </div>
                    </td>
                    <td
                      class="results border-r border-solid border-gray-300 pr-4 last:border-r-0"
                      v-else-if="indicator.percentage">
                      {{ filters.percentage(scenario.result, 100, 1, 1) }}
                    </td>
                    <td
                      class="results border-r border-solid border-gray-300 pr-4 last:border-r-0"
                      v-else>
                      {{ filters.compactMoney(scenario.result) }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { volatilityModelService } from '@/services/api';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import { Truncate } from '@/utilities/Helpers';
import * as htmltoimage from 'html-to-image';
import { compactMoney } from '@/utilities/Money';
import { filters } from '@/utilities/Filters';
import { inject } from 'vue';
import store from '@/store';
import ExportMenu from '@/components/ExportMenu.vue';
import { exportToPDF, exportToPNG, getChartImages, getHeaderImage, getPNG } from '@/utilities/ExportCharts';
import { VolatilityModel } from '@/types/VolatilityModel';
import { ChevronLeftIcon, Download, CircleHelp } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import { IsUserAnAdmin, UserHasPermission } from '@/utilities/AuthUtils';
import { orgName } from '@/utilities/OrgConfig';
import { UserPermission } from '@/types/UserPermissions';

interface Data {
  filters: any;
  loading: boolean;
  model?: VolatilityModel | null;
  selectedCategoryType?: number;
  exportMenuOptions: string[];
  isExporting: boolean;
}

interface Indicator {
  title: string;
  value: number;
  scenarios: { result: number; impact: number; threshold: boolean; retention?: number }[];
  warning?: string;
  percentage?: boolean;
}

export default {
  name: 'VolatilityShow',
  components: { AppButton, ExportMenu, PageSpinner, ChevronLeftIcon, Download, CircleHelp },
  data(): Data {
    return {
      filters,
      loading: true,
      model: undefined,
      selectedCategoryType: inject('selectedCategoryType'),
      exportMenuOptions: ['KPI Panel', 'Chart', 'Details Table'],
      isExporting: false,
    };
  },
  computed: {
    dataSource() {
      const chart = {
        animation: '0',
        caption: `Volatility Model: ${Truncate(this.model?.accountName ?? '', 30)}`,
        xAxisName: 'Annual Performance Indicator',
        yAxisName: 'Impact %',
        theme: 'cacs',
        paletteColors: ['#90d5e3', '#00748C', '#024b59', '#02272e'],
        legendNumColumns: 1,
        legendPosition: 'right-top',
        numberSuffix: '%',
        decimals: 1,
        exportFilename: `${this.model?.accountName ?? ''} Volatility Model`,
      };
      const category = this.indicators.map((i) => ({ label: i.title }));
      const categories = [{ category }];
      const dataset = this.scenarios.map((scenario, n) => ({
        seriesName: `Scenario ${String.fromCharCode(64 + scenario.num)} ${compactMoney(scenario.retention)} `,
        data: this.indicators.map((i) => ({ value: i.scenarios[n].impact * 100 })),
      }));
      const trendlines = [
        {
          line: [
            {
              startvalue: -((this.model?.maximumTolerance ?? 0) * 100),
              color: '#415363',
              displayvalue: 'Maximum{br}Tolerance',
              valueOnRight: '1',
              thickness: '3',
              showBelow: '1',
            },
          ],
        },
      ];
      return { categories, chart, dataset, trendlines };
    },
    canEditModel() {
      const user = store.state.user;
      if (IsUserAnAdmin(user) || UserHasPermission(user, UserPermission.AnalyticsWrite)) return true;
      // Client users on the account-scoped route can edit a model for an account they have access to.
      return !!this.$route.params.id && UserHasPermission(user, UserPermission.AnalyticsRead);
    },
    editRoute() {
      if (this.selectedCategoryType) {
        return this.$route.params.id
          ? `/account/${this.model?.accountId}/analytics/category/${this.$route.params.categoryId}/volatility/${this.model?.id}/edit`
          : `/analytics/category/${this.$route.params.categoryId}/volatility/${this.model?.id}/edit`;
      } else {
        return `/volatility/${this.model?.id}/edit`;
      }
    },
    scenarios() {
      return [1, 2, 3, 4]
        .map((n) => {
          const retention = this.retention(n);
          const title = `Scenario ${String.fromCharCode(64 + n)} ${retention ? compactMoney(retention) : ''}`;
          if (retention) {
            const taxRelief = retention * (this.model?.effectiveTaxRate ?? 0);
            const afterTax = retention - taxRelief;
            return { num: n, title, retention, taxRelief, afterTax };
          }
          return { num: n, title, retention: 0, taxRelief: 0, afterTax: 0 };
        })
        .filter((scenario) => scenario.retention > 0);
    },
    indicators(): Indicator[] {
      return [
        this.calcOperatingIncome(),
        this.calcNetIncome(),
        this.calcEbita(),
        this.calcCashFlowFromOperations(),
        this.calcFreeCashFlow(),
        this.calcEarningsPerShare(),
        this.calcEbitdaMargin(),
      ].filter((r) => r && r.value);
    },
    retentionPer() {
      return 10000000;
    },
    retentionPerTaxRelief() {
      return this.retentionPer * (this.model?.effectiveTaxRate ?? 0);
    },
    retentionPerAfterTax() {
      return this.retentionPer - this.retentionPerTaxRelief;
    },
    emptyScenarios() {
      return [1, 2, 3, 4].map(() => ({ result: 0, impact: 0, threshold: false }));
    },
  },
  methods: {
    retention(n) {
      return this.model && this.model[`retention${n}`];
    },
    onBackButtonClick() {
      if (this.selectedCategoryType) {
        return this.$route.params.id
          ? this.$router.push(`/account/${this.model?.accountId}/analytics/category/${this.selectedCategoryType}`)
          : this.$router.push(`/analytics/category/${this.selectedCategoryType}`);
      } else {
        return this.$route.params.id
          ? this.$router.push(`/account/${this.model?.accountId}/analytics`)
          : this.$router.push('/analytics');
      }
    },
    calcScenarios(value, impactPer) {
      return [1, 2, 3, 4]
        .map((n) => {
          const retention = this.retention(n);
          if (retention) {
            const result = value * (1 + impactPer * (retention / this.retentionPer));
            const impact = (result - value) / Math.abs(value);
            const threshold = impact < -(this.model?.maximumTolerance ?? 0);
            return { result, impact, threshold, retention };
          }
          return { result: 0, impact: 0, threshold: false, retention: 0 };
        })
        .filter((scenario) => scenario.retention > 0);
    },
    calcOperatingIncome(): Indicator {
      const value = this.model?.operatingIncome ?? 0;
      const impactPer = -this.retentionPer / value;
      const scenarios = this.calcScenarios(value, impactPer);
      return { title: 'Operating Income', value, scenarios };
    },
    calcNetIncome(): Indicator {
      const value = this.model?.netIncome ?? 0;
      const impactPer = -this.retentionPerAfterTax / value;
      const scenarios = this.calcScenarios(value, impactPer);
      return { title: 'Net Income', value, scenarios };
    },
    calcEbita(): Indicator {
      const value = this.model?.ebitda ?? 0;
      const impactPer = -this.retentionPer / value;
      const scenarios = this.calcScenarios(value, impactPer);
      return { title: 'EBITDA', value, scenarios };
    },
    calcCashFlowFromOperations(): Indicator {
      const value = this.model?.cashFlowFromOperations ?? 0;
      const impactPer = -this.retentionPerAfterTax / value;
      const scenarios = this.calcScenarios(value, impactPer);
      return { title: 'Cash Flow From Operations', value, scenarios };
    },
    calcFreeCashFlow(): Indicator {
      const value = this.model?.freeCashFlow ?? 0;
      const impactPer = -this.retentionPerAfterTax / value;
      const scenarios = this.calcScenarios(value, impactPer);
      return { title: 'Free Cash Flow', value, scenarios };
    },
    calcEarningsPerShare(): Indicator {
      const value = this.model?.earningsPerShare ?? 0;
      if (this.model?.sharesOutstanding) {
        const impactPer = -this.retentionPerAfterTax / (value * this.model?.sharesOutstanding);
        const scenarios = this.calcScenarios(value, impactPer);
        return { title: 'Earnings Per Share', value, scenarios };
      }
      return {
        title: 'Earnings Per Share',
        value,
        scenarios: this.emptyScenarios,
        warning: 'Requires Shares Outstanding',
      };
    },
    calcEbitdaMargin(): Indicator {
      const value = this.model?.ebitdaMargin ?? 0;
      if (this.model?.annualRevenue) {
        const impactPer = -this.retentionPer / (value * this.model?.annualRevenue);
        const scenarios = this.calcScenarios(value, impactPer);
        return { title: 'EBITDA Margin (%)', percentage: true, value, scenarios };
      }
      return {
        title: 'EBITDA Margin (%)',
        percentage: true,
        value,
        scenarios: this.emptyScenarios,
        warning: 'Requires Annual Revenue',
      };
    },
    async refresh() {
      this.loading = true;
      this.model = await volatilityModelService.findById(this.$route.params.volatilityId as string);
      this.loading = false;
    },
    async downloadTable() {
      const elem = document.getElementById('vol-tbl');
      if (!elem) return;

      const uri = await (htmltoimage as any).toPng(elem, {
        windowWidth: window.innerWidth + 100,
        windowHeight: window.innerHeight,
        backgroundColor: 'white',
        skipFonts: true,
      } as any);
      console.log(uri);
      const filename = `${this.model?.accountName}-${this.model?.name}.png`;
      var link = document.createElement('a');
      if (typeof link.download === 'string') {
        link.href = uri;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(uri);
      }
    },
    async handleExport(type, charts, finishExport) {
      this.isExporting = true;
      // gather element IDs for each chart
      const elementIds = charts
        .map((c) => {
          if (c === 'KPI Panel') {
            return 'kpiPanel';
          } else if (c === 'Chart') {
            return 'volatilityChart';
          } else if (c === 'Details Table') {
            return 'vol-tbl';
          } else {
            console.error(`unexpected chart ${c}`);
          }
        })
        .filter((id) => id !== null);

      // create the PNG image that will be used for exporting
      const images = await getChartImages(elementIds);

      const maxWidth = Math.max(...images.map(({ width }) => width));
      const header = await getHeaderImage(maxWidth, this.model?.name ?? '');

      // create the PNG image that will be used for exporting
      const png = await getPNG(images, header);

      const currentDate = new Date();
      const formattedDate = currentDate.toISOString().split('T')[0].replace(/-/g, '.');
      const org = orgName();
      const filename = `${org ? org + '-' : ''}${this.model?.name}_${formattedDate}`;

      if (type === 'png') {
        await exportToPNG(png, filename + '.png');
      } else if (type === 'pdf') {
        await exportToPDF(images, header, filename + '.pdf');
      } else {
        console.log(`unexpected value ${type} for export type`);
      }

      this.isExporting = false;
      finishExport();
    },
  },
  created() {
    return this.refresh();
  },
};
</script>

<style scoped>
.volatility-container {
  padding: 10px;
  width: -webkit-fill-available;
}

.header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  padding-bottom: 0.5rem;
  align-content: center;
  align-items: center;
}

.header > h1 {
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
}

.header > .spacer {
  padding: 0 1rem;
}

.parameter-list {
  display: flex;
}

.parameter-list > .parameter {
  text-align: center;
  padding-left: 2rem;
}

.parameter-list > .parameter > .label {
  text-transform: uppercase;
  font-size: 0.8em;
}

.parameter-list > .parameter > .value {
  font-weight: 700;
  font-size: 1.2rem;
}

.scenario-title {
  font-weight: 700;
  font-size: 1.1em;
  text-align: center;
}

.scenarios {
  overflow-x: auto;
}

.scenarios > table > tbody > tr > td {
  /*padding: 0.25rem;*/
}

.scenarios > table > tbody > tr.values:hover > td {
  background: #e7e7e7;
}

.stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 1rem;
  align-content: center;
  align-items: center;
  justify-items: center;
  margin: 0.25rem 0;
  padding: 0.5rem 0.5rem 0.25rem 0.5rem;
  min-width: 275px;
  background: #f5f5f5;
  border-top: 1px solid #e7e7e7;
  border-bottom: 1px solid #e7e7e7;
}

.stats .stat .label,
.stats .stat .value {
  display: block;
}

.stats .stat .label {
  text-transform: uppercase;
  font-size: 0.6em;
}

.stats .stat .value {
  font-weight: 700;
  font-size: 1.1em;
}

.indicator-title-header {
  padding-right: 1rem;
  text-transform: uppercase;
  font-size: 0.8em;
}

.indicator-title {
  font-weight: 700;
}

.impact-header,
.results-header,
.indicator-value-header {
  text-transform: uppercase;
  font-size: 0.8em;
}

.impact-header,
.results-header,
.indicator-value-header,
.impact,
.results,
.indicator-value {
  text-align: right;
  /*padding-right: 0.5rem;*/
}

.results,
.indicator-value {
  font-weight: 700;
}

tr.values td {
  padding-bottom: 0.25rem;
}

.indicator-value-header div,
.indicator-value div {
  padding-right: 1rem;
  /*min-width: 250px;*/
}
</style>
